import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, PhoneIncoming, PhoneForwarded, Timer, Wallet, Radio,
  ArrowUpRight, ArrowRight, ArrowLeft, TrendingUp, CircleDot,
  CalendarClock, Zap, ShieldCheck, MicVocal, ChevronRight, Check,
  Globe2, Loader2, Sparkles, FileDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * /dashboard/live-calls — buyer + publisher pay-per-call overview.
 *
 * Displays deterministic simulated data since the marketplace is pre-launch:
 *   · Buyer view — active campaigns, billable calls today, quality metrics
 *   · Publisher view — Friday payout preview, weekly billable, top sectors
 *
 * Data is derived from a deterministic seed tied to the current hour so
 * refreshes feel alive without being random. Real integration hooks will
 * replace SIM_* helpers once the pay-per-call routing engine ships.
 */

/* ─── DETERMINISTIC MOCK DATA ─── */

function useSimTick(intervalMs = 4000) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return tick;
}

// Simulated buyer campaigns
function useBuyerCampaigns(tick) {
  return useMemo(() => [
    { id: "c1", sector: "Auto Insurance",       geo: "🇬🇧 UK",  bidCap: 42, dailyCap: 150, todayCalls: 87  + (tick % 5), billable: 61 + (tick % 4), status: "live" },
    { id: "c2", sector: "Life Insurance",       geo: "🇬🇧 UK",  bidCap: 58, dailyCap: 80,  todayCalls: 47  + (tick % 3), billable: 32 + (tick % 3), status: "live" },
    { id: "c3", sector: "Debt / Credit",        geo: "🇬🇧 UK",  bidCap: 65, dailyCap: 60,  todayCalls: 38  + (tick % 2), billable: 27 + (tick % 2), status: "live" },
    { id: "c4", sector: "Medicare / Health",    geo: "🇺🇸 US",  bidCap: 68, dailyCap: 200, todayCalls: 124 + (tick % 6), billable: 89 + (tick % 5), status: "live" },
    { id: "c5", sector: "Mortgages",            geo: "🇬🇧 UK",  bidCap: 92, dailyCap: 40,  todayCalls: 22,                billable: 16,               status: "paused" },
  ], [tick]);
}

// Simulated Friday payout for publisher view
function usePublisherPayout(tick) {
  return useMemo(() => {
    const rows = [
      { id: "p1", sector: "Auto Insurance",  calls: 214 + (tick % 3), avg: 22.4, gross: 4793 + (tick * 22) },
      { id: "p2", sector: "Home Insurance",  calls: 158 + (tick % 2), avg: 28.1, gross: 4439 + (tick * 28) },
      { id: "p3", sector: "Life Insurance",  calls: 96,               avg: 34.8, gross: 3341            },
      { id: "p4", sector: "Solar",           calls: 74,               avg: 52.6, gross: 3892            },
      { id: "p5", sector: "Roofing",         calls: 41,               avg: 61.3, gross: 2513            },
    ];
    const total = rows.reduce((s, r) => s + r.gross, 0);
    return { rows, total, fees: Math.round(total * 0.02), net: Math.round(total * 0.98) };
  }, [tick]);
}

/* ─────────────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────────────── */
export default function LiveCallsDashboard() {
  const [view, setView] = useState("buyer"); // buyer | publisher
  const [wizardOpen, setWizardOpen] = useState(false);
  const tick = useSimTick(4000);
  const campaigns = useBuyerCampaigns(tick);
  const payout = usePublisherPayout(tick);

  // Aggregate today's buyer numbers
  const todayTotals = useMemo(() => {
    const live = campaigns.filter((c) => c.status === "live");
    const calls   = campaigns.reduce((s, c) => s + c.todayCalls, 0);
    const billed  = campaigns.reduce((s, c) => s + c.billable, 0);
    const spend   = campaigns.reduce((s, c) => s + c.billable * (c.bidCap * 0.72), 0);
    return {
      activeCampaigns: live.length,
      totalCampaigns:  campaigns.length,
      calls,
      billed,
      billRate: calls > 0 ? Math.round((billed / calls) * 1000) / 10 : 0,
      spend: Math.round(spend),
    };
  }, [campaigns]);

  // Days to Friday for the payout headline
  const daysToFri = useMemo(() => {
    const d = new Date();
    const diff = (5 - d.getDay() + 7) % 7; // 5 = Fri
    return diff === 0 ? "today" : `${diff}d`;
  }, []);

  return (
    <div className="space-y-6" data-testid="live-calls-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#0055FF]">
            <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Pay-per-call · live
          </div>
          <h1 className="mt-2 text-3xl sm:text-4xl tracking-[-0.025em] font-semibold text-slate-900">
            Live Calls
          </h1>
          <p className="mt-2 text-[14.5px] text-slate-600 max-w-2xl">
            Your pay-per-call campaigns as a buyer, and your publisher-side payout preview — one dashboard, both sides
            of the marketplace.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/pay-per-call">
            <Button variant="outline" className="rounded-full h-9 px-3.5 text-[13px] border-slate-300" data-testid="lc-view-marketplace">
              View marketplace <ArrowUpRight className="size-3.5" />
            </Button>
          </Link>
        </div>
      </div>

      {/* View toggle */}
      <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="lc-view-toggle">
        <button
          onClick={() => setView("buyer")}
          className={`h-9 px-4 rounded-full text-[13px] font-semibold transition-all inline-flex items-center gap-1.5 ${view === "buyer" ? "bg-[#0055FF] text-white shadow-[0_4px_16px_rgba(0,85,255,0.35)]" : "text-slate-600 hover:text-slate-900"}`}
          data-testid="lc-toggle-buyer"
        >
          <PhoneIncoming className="size-3.5" /> As Buyer
        </button>
        <button
          onClick={() => setView("publisher")}
          className={`h-9 px-4 rounded-full text-[13px] font-semibold transition-all inline-flex items-center gap-1.5 ${view === "publisher" ? "bg-slate-900 text-white shadow-[0_4px_16px_rgba(15,23,42,0.35)]" : "text-slate-600 hover:text-slate-900"}`}
          data-testid="lc-toggle-publisher"
        >
          <PhoneForwarded className="size-3.5" /> As Publisher
        </button>
      </div>

      {view === "buyer" ? (
        <BuyerView totals={todayTotals} campaigns={campaigns} tick={tick} onNewCampaign={() => setWizardOpen(true)} />
      ) : (
        <PublisherView payout={payout} daysToFri={daysToFri} />
      )}

      <NewCampaignWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   BUYER VIEW
   ═════════════════════════════════════════════════════════════════ */
function BuyerView({ totals, campaigns, tick, onNewCampaign }) {
  // Deterministic, alive-feeling quality metrics — subtle drift only.
  // Adds ±0.1% jitter to consent%, ±3ms to ping-post, DNC stays pinned at 100.
  const consentPct  = (99.7 + ((tick % 4) - 1.5) * 0.05).toFixed(1);   // 99.60 → 99.85
  const pingPostMs  = 218 + ((tick % 5) - 2) * 3;                       // 212 → 224
  const dncPct      = "100.0";
  return (
    <>
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          testid="lc-kpi-active-campaigns"
          Icon={Radio}
          label="Active campaigns"
          value={totals.activeCampaigns}
          sub={`${totals.totalCampaigns} total (incl. paused)`}
          highlight
        />
        <Kpi
          testid="lc-kpi-calls-today"
          Icon={Phone}
          label="Calls today"
          value={totals.calls.toLocaleString()}
          sub={<span className="inline-flex items-center gap-1"><TrendingUp className="size-3 text-emerald-600" /> vs. yesterday</span>}
        />
        <Kpi
          testid="lc-kpi-billable"
          Icon={Timer}
          label="Billable calls"
          value={totals.billed.toLocaleString()}
          sub={`${totals.billRate}% conversion`}
        />
        <Kpi
          testid="lc-kpi-spend"
          Icon={Wallet}
          label="Spend today"
          value={`£${totals.spend.toLocaleString()}`}
          sub="Debited from wallet"
        />
      </div>

      {/* Active campaigns table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="lc-buyer-campaigns">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Your campaigns</div>
            <div className="mt-0.5 text-[15px] font-semibold text-slate-900">Live routing rules</div>
          </div>
          <Button variant="outline" onClick={onNewCampaign} className="rounded-full h-8 px-3.5 text-[12.5px] border-slate-300" data-testid="lc-new-campaign">
            + New campaign
          </Button>
        </div>
        <div className="hidden md:grid grid-cols-[1.4fr_0.8fr_0.6fr_0.7fr_0.7fr_0.6fr_0.5fr] gap-3 px-5 py-3 text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 bg-slate-50/60 border-b border-slate-100">
          <div>Sector</div>
          <div>Geo</div>
          <div>Bid cap</div>
          <div>Today</div>
          <div>Billable</div>
          <div>Daily cap</div>
          <div>Status</div>
        </div>
        <div className="divide-y divide-slate-100">
          {campaigns.map((c) => (
            <div key={c.id} className="grid md:grid-cols-[1.4fr_0.8fr_0.6fr_0.7fr_0.7fr_0.6fr_0.5fr] gap-2 md:gap-3 px-5 py-4 md:items-center text-[13.5px]" data-testid={`lc-campaign-${c.id}`}>
              {/* Mobile-friendly labels — visible only < md */}
              <div className="flex items-center justify-between md:block">
                <span className="font-semibold text-slate-900">{c.sector}</span>
                <span className="md:hidden"><StatusChip status={c.status} /></span>
              </div>
              <div className="flex items-center gap-2 md:block text-slate-600 md:text-inherit">
                <span className="md:hidden text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Geo</span>
                <span>{c.geo}</span>
              </div>
              <div className="flex items-center gap-2 md:block">
                <span className="md:hidden text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Bid cap</span>
                <span className="font-mono text-slate-900">£{c.bidCap}</span>
              </div>
              <div className="flex items-center gap-2 md:block">
                <span className="md:hidden text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Today</span>
                <span className="font-mono tabular-nums text-slate-900">{c.todayCalls}</span>
              </div>
              <div className="flex items-center gap-2 md:block">
                <span className="md:hidden text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Billable</span>
                <span className="font-mono tabular-nums text-slate-900">
                  {c.billable}
                  <span className="ml-1 text-[11px] text-slate-500">
                    ({c.todayCalls > 0 ? Math.round((c.billable / c.todayCalls) * 100) : 0}%)
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2 md:block">
                <span className="md:hidden text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Daily cap</span>
                <span className="font-mono tabular-nums text-slate-600">{c.dailyCap}</span>
              </div>
              <div className="hidden md:block">
                <StatusChip status={c.status} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quality signals */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="lc-quality-consent">
          <div className="flex items-center gap-2">
            <span className="inline-grid place-items-center size-8 rounded-lg bg-[#0055FF]/[0.08] text-[#0055FF] ring-1 ring-[#0055FF]/15">
              <MicVocal className="size-4" strokeWidth={1.7} />
            </span>
            <div className="text-[13px] font-semibold text-slate-900">Consent captured</div>
            <span className="ml-auto inline-flex items-center gap-1 text-[9.5px] font-mono text-emerald-600 font-semibold">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> live
            </span>
          </div>
          <div className="mt-4 text-[26px] tracking-[-0.025em] font-semibold text-slate-900 tabular-nums">{consentPct}%</div>
          <div className="mt-1 text-[12px] text-slate-500">TCPA + PECR verified on every call</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="lc-quality-ping">
          <div className="flex items-center gap-2">
            <span className="inline-grid place-items-center size-8 rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200">
              <Zap className="size-4" strokeWidth={1.7} />
            </span>
            <div className="text-[13px] font-semibold text-slate-900">Avg routing latency</div>
            <span className="ml-auto inline-flex items-center gap-1 text-[9.5px] font-mono text-emerald-600 font-semibold">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> live
            </span>
          </div>
          <div className="mt-4 text-[26px] tracking-[-0.025em] font-semibold text-slate-900 tabular-nums">{pingPostMs}ms</div>
          <div className="mt-1 text-[12px] text-slate-500">Buyer matched faster than caller hears IVR</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="lc-quality-dnc">
          <div className="flex items-center gap-2">
            <span className="inline-grid place-items-center size-8 rounded-lg bg-[#F5B300]/10 text-[#F5B300] ring-1 ring-[#F5B300]/20">
              <ShieldCheck className="size-4" strokeWidth={1.7} />
            </span>
            <div className="text-[13px] font-semibold text-slate-900">DNC scrubbed</div>
            <span className="ml-auto inline-flex items-center gap-1 text-[9.5px] font-mono text-emerald-600 font-semibold">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> live
            </span>
          </div>
          <div className="mt-4 text-[26px] tracking-[-0.025em] font-semibold text-slate-900 tabular-nums">{dncPct}%</div>
          <div className="mt-1 text-[12px] text-slate-500">Every call washed against 4 sources before routing</div>
        </div>
      </div>
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   PUBLISHER VIEW · Friday payout preview
   ═════════════════════════════════════════════════════════════════ */
function PublisherView({ payout, daysToFri }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const nextFri = useMemo(() => {
    const d = new Date();
    const diff = (5 - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + diff);
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
  }, []);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const me = JSON.parse(localStorage.getItem("connexis_user") || "{}");
      const { api } = await import("@/lib/api");
      const resp = await api.post(
        "/pay-per-call/payout-pdf",
        {
          net_gbp: payout.net,
          gross_gbp: payout.total,
          fee_gbp: payout.fees,
          week_ending: nextFri,
          publisher_name: me.company || me.name || "Publisher",
          rows: payout.rows.map((r) => ({
            sector: r.sector, calls: r.calls, avg_gbp: r.avg, gross_gbp: r.gross,
          })),
        },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `connexis-payout-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
    } catch (e) {
      toast.error("Couldn't generate PDF — try again in a moment");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <>
      {/* Hero payout card — dark, /solutions-style */}
      <div className="relative rounded-2xl bg-gradient-to-br from-slate-900 to-[#0c1b3a] text-white p-7 sm:p-9 overflow-hidden" data-testid="lc-publisher-payout">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-400/25 blur-3xl pointer-events-none" />

        <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-8 items-end">
          <div>
            <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-[#7da9ff] font-semibold">
              <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Friday payout · {daysToFri === "today" ? "today" : `in ${daysToFri}`}
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">Net to you</span>
              <span className="text-[10.5px] font-mono text-white/40">· {nextFri}</span>
            </div>
            <div className="mt-2 text-[64px] tracking-[-0.045em] leading-none font-semibold text-white tabular-nums" data-testid="lc-payout-net">
              £{payout.net.toLocaleString()}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px] text-white/55 font-mono">
              <span>Gross <span className="text-white/85">£{payout.total.toLocaleString()}</span></span>
              <span className="text-white/25">·</span>
              <span>Platform fee <span className="text-white/85">£{payout.fees.toLocaleString()}</span></span>
              <span className="text-white/25">·</span>
              <span>{payout.rows.reduce((s, r) => s + r.calls, 0).toLocaleString()} billable calls</span>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button
                onClick={downloadPdf}
                disabled={pdfBusy}
                className="rounded-full h-10 px-4 bg-white text-slate-900 hover:bg-slate-100 text-[13px] font-semibold gap-1.5"
                data-testid="lc-payout-pdf"
              >
                {pdfBusy ? <Loader2 className="size-4 animate-spin" /> : <><FileDown className="size-4" /> Download remittance PDF</>}
              </Button>
              <Button variant="outline" className="rounded-full h-10 px-4 border-white/20 bg-transparent hover:bg-white/10 text-white text-[13px]">
                Payout history
              </Button>
            </div>
          </div>

          {/* Compact info card on the right */}
          <div className="rounded-xl bg-white/[0.06] ring-1 ring-white/10 backdrop-blur p-5">
            <div className="flex items-center gap-2">
              <CalendarClock className="size-4 text-[#7da9ff]" />
              <span className="text-[12px] font-semibold text-white/85">Payout schedule</span>
            </div>
            <ul className="mt-4 space-y-2.5 text-[13px] text-white/85">
              <li className="flex items-center justify-between">
                <span className="text-white/60">Cadence</span>
                <span className="font-medium">Weekly · Fri</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-white/60">Rail</span>
                <span className="font-medium">Stripe Connect</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-white/60">Minimum</span>
                <span className="font-medium">None</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-white/60">Retention</span>
                <span className="font-medium">Rolling 7 days</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Sector breakdown */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="lc-publisher-sectors">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">This week</div>
          <div className="mt-0.5 text-[15px] font-semibold text-slate-900">Payout by sector</div>
        </div>
        <div className="hidden md:grid grid-cols-[1.4fr_0.8fr_0.8fr_1fr] gap-3 px-5 py-3 text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500 bg-slate-50/60 border-b border-slate-100">
          <div>Sector</div>
          <div>Billable calls</div>
          <div>Avg payout</div>
          <div>Gross</div>
        </div>
        <div className="divide-y divide-slate-100">
          {payout.rows.map((r) => (
            <div key={r.id} className="grid md:grid-cols-[1.4fr_0.8fr_0.8fr_1fr] gap-2 md:gap-3 px-5 py-4 md:items-center text-[13.5px]" data-testid={`lc-payout-row-${r.id}`}>
              <div className="flex items-center justify-between md:block">
                <span className="font-semibold text-slate-900">{r.sector}</span>
                <span className="md:hidden font-mono tabular-nums font-semibold text-slate-900">£{r.gross.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2 md:block">
                <span className="md:hidden text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Billable calls</span>
                <span className="font-mono tabular-nums text-slate-900">{r.calls.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2 md:block">
                <span className="md:hidden text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Avg payout</span>
                <span className="font-mono tabular-nums text-slate-600">£{r.avg.toFixed(1)}</span>
              </div>
              <div className="hidden md:block font-mono tabular-nums font-semibold text-slate-900">£{r.gross.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════
   SHARED BITS
   ═════════════════════════════════════════════════════════════════ */
function Kpi({ Icon, label, value, sub, highlight = false, testid }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${highlight ? "border-[#0055FF]/25 bg-[#0055FF]/[0.03]" : "border-slate-200 bg-white"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-grid place-items-center size-8 rounded-lg ${highlight ? "bg-[#0055FF]/10 text-[#0055FF] ring-1 ring-[#0055FF]/15" : "bg-slate-50 text-slate-600 ring-1 ring-slate-200"}`}>
          <Icon className="size-4" strokeWidth={1.7} />
        </span>
        <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</span>
      </div>
      <div className="mt-3 text-[26px] tracking-[-0.025em] font-semibold text-slate-900 tabular-nums">{value}</div>
      <div className="mt-1 text-[12px] text-slate-500 leading-snug">{sub}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const tone =
    status === "live"   ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    status === "paused" ? "bg-slate-100 text-slate-600 border-slate-200" :
                          "bg-[#0055FF]/10 text-[#0055FF] border-[#0055FF]/20";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${tone} px-2 py-0.5 text-[11px] font-medium capitalize`}>
      <CircleDot className="size-2.5" strokeWidth={2.5} />
      {status}
    </span>
  );
}

/* ═════════════════════════════════════════════════════════════════
   NEW CAMPAIGN WIZARD — 4 steps · sector → geo → duration → daily cap
   All state client-side; on submit posts to /api/pay-per-call/enquiry
   in "buyer" mode so it lands in the same admin queue.
   ═════════════════════════════════════════════════════════════════ */

const WIZ_SECTORS = [
  "Auto Insurance", "Home Insurance", "Life Insurance", "Final Expense",
  "Medicare / Health", "Auto Warranty", "Home Warranty",
  "Mortgages", "Reverse Mortgage", "Auto Loans / Refi", "Debt / Credit",
  "Credit Repair", "Tax Debt Relief", "Business Finance",
  "Solar", "Roofing", "HVAC / Plumbing", "Windows & Doors",
  "Bath / Kitchen Remodel", "Home Security", "Water Damage / Restoration",
  "Legal · Personal Injury", "Legal · Family Law", "Rehab & Treatment",
];
const WIZ_GEOS = [
  { label: "United Kingdom", flag: "🇬🇧" },
  { label: "United States",  flag: "🇺🇸" },
  { label: "Canada",         flag: "🇨🇦" },
  { label: "Australia",      flag: "🇦🇺" },
  { label: "Ireland",        flag: "🇮🇪" },
];
const WIZ_DURATIONS = [
  { s: 60,  label: "60s",  desc: "Highest volume · lower quality bar" },
  { s: 90,  label: "90s",  desc: "Balanced — the market default" },
  { s: 120, label: "120s", desc: "Highest quality · lower volume" },
];

function NewCampaignWizard({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [sector, setSector] = useState(null);
  const [geo, setGeo] = useState("United Kingdom");
  const [duration, setDuration] = useState(90);
  const [dailyCap, setDailyCap] = useState(50);
  const [bidCap, setBidCap] = useState(45);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(0); setSector(null); setGeo("United Kingdom");
        setDuration(90); setDailyCap(50); setBidCap(45);
        setBusy(false); setDone(false);
      }, 300);
    }
  }, [open]);

  const canAdvance = [Boolean(sector), Boolean(geo), Boolean(duration), dailyCap > 0 && bidCap > 0][step];

  const submit = async () => {
    setBusy(true);
    try {
      // We reuse the existing pay-per-call enquiry endpoint so admins see it
      // in the same inbox and can approve → activate. Also flag `source` so
      // ops can distinguish self-serve wizard from marketing-form enquiries.
      const me = JSON.parse(localStorage.getItem("connexis_user") || "{}");
      const { api } = await import("@/lib/api");
      await api.post("/pay-per-call/enquiry", {
        mode: "buyer",
        name: me.name || "Dashboard user",
        email: me.email || "unknown@connex.is",
        company: me.company || "",
        country: geo,
        buyer_sectors: sector ? [sector.toLowerCase().replace(/[^a-z0-9]+/g, "-")] : [],
        buyer_monthly_calls: `${dailyCap * 22} calls / mo`,
        buyer_target_cpc_gbp: `£${bidCap}`,
        message: `New campaign draft — Duration threshold ${duration}s · daily cap ${dailyCap} · bid cap £${bidCap}`,
      });
      setDone(true);
    } catch (e) {
      toast.error("Couldn't submit — please try again");
    } finally {
      setBusy(false);
    }
  };

  const STEPS = ["Sector", "Geo", "Duration", "Caps"];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="lc-wizard">
        {done ? (
          <div className="p-10 text-center">
            <span className="mx-auto grid place-items-center size-14 rounded-full bg-emerald-50 text-emerald-600">
              <Check className="size-7" strokeWidth={2} />
            </span>
            <h3 className="mt-5 text-2xl font-semibold text-slate-900 tracking-tight">Campaign submitted.</h3>
            <p className="mt-3 text-[14px] text-slate-600 max-w-md mx-auto leading-relaxed">
              Ops will review your rules (sector · geo · duration · caps) and activate the campaign within one business
              day. You'll see it in the Live campaigns table with a Live status once it goes hot.
            </p>
            <Button onClick={onClose} className="mt-6 rounded-full h-10 px-5 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="lc-wizard-close">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
              <DialogTitle className="text-[18px] font-semibold tracking-tight">
                Create a pay-per-call campaign
              </DialogTitle>
              {/* Step tracker — labels hidden on very small viewports */}
              <div className="mt-4 flex items-center gap-2" data-testid="lc-wizard-steps">
                {STEPS.map((s, i) => (
                  <React.Fragment key={s}>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-grid place-items-center size-6 rounded-full text-[11px] font-mono font-semibold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-[#0055FF] text-white" : "bg-slate-100 text-slate-500"}`}>
                        {i < step ? <Check className="size-3" strokeWidth={3} /> : i + 1}
                      </span>
                      <span className={`hidden sm:inline text-[12px] ${i === step ? "font-semibold text-slate-900" : "text-slate-500"}`}>{s}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
                  </React.Fragment>
                ))}
              </div>
            </DialogHeader>

            <div className="px-6 py-6 min-h-[280px]">
              {step === 0 && (
                <div data-testid="lc-wizard-step-sector">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Step 1 of 4</div>
                  <h4 className="mt-1.5 text-[17px] font-semibold text-slate-900">Which sector are you buying?</h4>
                  <p className="mt-1 text-[13px] text-slate-500">Every campaign runs on one primary sector. You can spin up more later.</p>
                  <div className="mt-5 grid sm:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
                    {WIZ_SECTORS.map((s) => (
                      <button
                        key={s}
                        onClick={() => setSector(s)}
                        className={`text-left rounded-lg border px-3 py-2.5 text-[13px] transition-all ${sector === s ? "border-[#0055FF] bg-[#0055FF]/[0.04] text-slate-900" : "border-slate-200 bg-white hover:border-[#0055FF]/40"}`}
                        data-testid={`lc-wizard-sector-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="lc-wizard-step-geo">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Step 2 of 4</div>
                  <h4 className="mt-1.5 text-[17px] font-semibold text-slate-900">Which country?</h4>
                  <p className="mt-1 text-[13px] text-slate-500">Sets the compliance layer (TCPA, PECR, CASL…) and the buyer pool.</p>
                  <div className="mt-5 grid sm:grid-cols-2 gap-2">
                    {WIZ_GEOS.map((g) => (
                      <button
                        key={g.label}
                        onClick={() => setGeo(g.label)}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-3 text-[13px] transition-all ${geo === g.label ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-slate-200 bg-white hover:border-[#0055FF]/40"}`}
                        data-testid={`lc-wizard-geo-${g.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span className="text-xl leading-none">{g.flag}</span>
                        <span className="font-medium text-slate-900">{g.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="lc-wizard-step-duration">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Step 3 of 4</div>
                  <h4 className="mt-1.5 text-[17px] font-semibold text-slate-900">Set your buyer-duration threshold.</h4>
                  <p className="mt-1 text-[13px] text-slate-500">You only pay for calls that stay connected past this point.</p>
                  <div className="mt-5 grid gap-2">
                    {WIZ_DURATIONS.map((d) => (
                      <button
                        key={d.s}
                        onClick={() => setDuration(d.s)}
                        className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-all ${duration === d.s ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-slate-200 bg-white hover:border-[#0055FF]/40"}`}
                        data-testid={`lc-wizard-dur-${d.s}`}
                      >
                        <div>
                          <div className="text-[15px] font-semibold text-slate-900">{d.label}</div>
                          <div className="text-[12px] text-slate-500 mt-0.5">{d.desc}</div>
                        </div>
                        <Timer className={`size-5 ${duration === d.s ? "text-[#0055FF]" : "text-slate-400"}`} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="lc-wizard-step-caps">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Step 4 of 4</div>
                  <h4 className="mt-1.5 text-[17px] font-semibold text-slate-900">Bid cap and daily cap.</h4>
                  <p className="mt-1 text-[13px] text-slate-500">We won't spend beyond either ceiling. You can change these any time.</p>
                  <div className="mt-5 grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lc-bidcap" className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-600">Max bid per call</Label>
                      <div className="relative mt-1.5">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-[13.5px] font-mono">£</span>
                        <Input
                          id="lc-bidcap" type="number" min="1" data-testid="lc-wizard-bidcap"
                          value={bidCap} onChange={(e) => setBidCap(Number(e.target.value) || 0)}
                          className="h-11 pl-6"
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1.5">Typical for {sector || "this sector"}: £14 – £160.</p>
                    </div>
                    <div>
                      <Label htmlFor="lc-dailycap" className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-600">Daily call cap</Label>
                      <Input
                        id="lc-dailycap" type="number" min="1" data-testid="lc-wizard-dailycap"
                        value={dailyCap} onChange={(e) => setDailyCap(Number(e.target.value) || 0)}
                        className="h-11 mt-1.5"
                      />
                      <p className="text-[11px] text-slate-500 mt-1.5">Max spend today ≈ £{(bidCap * dailyCap).toLocaleString()}.</p>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4">
                    <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-500">Campaign summary</div>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px] text-slate-700">
                      <div><span className="text-slate-500">Sector:</span> <span className="font-semibold text-slate-900">{sector}</span></div>
                      <div><span className="text-slate-500">Geo:</span> <span className="font-semibold text-slate-900">{geo}</span></div>
                      <div><span className="text-slate-500">Duration:</span> <span className="font-semibold text-slate-900">{duration}s billable</span></div>
                      <div><span className="text-slate-500">Bid cap:</span> <span className="font-semibold text-slate-900">£{bidCap}/call</span></div>
                      <div className="col-span-2"><span className="text-slate-500">Daily cap:</span> <span className="font-semibold text-slate-900">{dailyCap} calls · max £{(bidCap * dailyCap).toLocaleString()}/day</span></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 bg-slate-50/60">
              <Button
                variant="ghost"
                onClick={() => step > 0 ? setStep(step - 1) : onClose()}
                className="text-slate-600 hover:text-slate-900 h-9 px-3 text-[13px]"
                data-testid="lc-wizard-back"
              >
                {step === 0 ? "Cancel" : <><ArrowLeft className="size-4 mr-1" /> Back</>}
              </Button>
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canAdvance}
                  className="rounded-full h-10 px-5 bg-[#0055FF] hover:bg-[#0044CC] gap-1.5"
                  data-testid="lc-wizard-next"
                >
                  Continue <ArrowRight className="size-4" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={busy || !canAdvance}
                  className="rounded-full h-10 px-5 bg-[#0055FF] hover:bg-[#0044CC] gap-1.5"
                  data-testid="lc-wizard-submit"
                >
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <><Sparkles className="size-4" /> Submit for review</>}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
